import { Button, Card, Descriptions, Form, Input, Space, Table } from "antd";
import classNames from "classnames/bind";
import style from '../index.module.scss'
import { useTranslation } from "react-i18next";
import { InputCurrency } from "components/";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { CloseOutlined, EditOutlined, FileSyncOutlined, CloudUploadOutlined } from "@ant-design/icons";
import moment from "moment";
import { getDatesInRange } from "utils/date";
import { cloneDeep } from "lodash";
import { getUser } from "utils/";
import Table2XLSX from "./TableExport";
import ModalImport from "./ModalImport";
import * as XLSX from 'xlsx';
import { toast } from "react-toastify";
import inventoryApi from "api/inventory";
import { rolesImport } from "../roles";

const cx = classNames.bind(style)
const EditableContext = createContext(null);
export default function InventoryTable({ shop, shopInfo, month, inventoryInfo, listHoliday, notify, onSearch }) {
    const { t } = useTranslation()

    const [formImport] = Form.useForm();

    const [editMode, setEditMode] = useState(false)
    const [loading, setLoading] = useState(false)
    const [dataSource, setDataSource] = useState([])
    const [openImport, setOpenImport] = useState(false)


    const handleSyncData = useCallback(() => {
        const startDateOfMonth = moment(month, 'YYYY-MM').startOf('month').format('YYYY-MM-DD')
        const endDateOfMonth = moment(month, 'YYYY-MM').endOf('month').format('YYYY-MM-DD')

        const rangeDate = getDatesInRange(startDateOfMonth, endDateOfMonth)

        setDataSource(rangeDate.map((date, idx) => {
            const index = inventoryInfo?.list?.findIndex(item => moment(item.date).format('YYYY-MM-DD') === date)
            if (index !== -1) {
                return {
                    ...inventoryInfo?.list[index],
                    date
                }
            }

            return {
                date
            }
        }))

    }, [month, inventoryInfo])

    const onOpenImport = () => {
        setOpenImport(true)
    }

    const closeModalImport = () => {
        setOpenImport(false)
        formImport.resetFields()
    }

    useEffect(() => {
        handleSyncData()
    }, [handleSyncData, month, inventoryInfo])

    const toggleModeEdit = () => {
        setEditMode(true)
    }

    const saveEdit = async () => {
        try {
            setLoading(true)
            await inventoryApi.createInventory({
                shop,
                month,
                list: dataSource
            })
            notify("success", t('notification'), t('updateRevenueSuccess'));
            onSearch()
            setEditMode(false)
        } catch (error) {
            notify("error", t('error'), error?.response?.data?.message || t('hasError'));
        } finally {
            setLoading(false)
        }
    }

    const cancelEdit = () => {
        setEditMode(false)
        handleSyncData()
    }

    const handleSave = useCallback((row) => {
        const newData = cloneDeep(dataSource);
        const index = newData.findIndex((item) => row.date === item.date);
        const item = newData[index];
        newData.splice(index, 1, {
            ...item,
            ...row,
        });
        const res = newData.map((info, index) => {
            if (moment(month).startOf('month').isSame(moment(info.date))) {
                info.polo = {
                    ...info.polo,
                    inventory: inventoryInfo.previousPolo - (info?.polo?.used || 0) + (info?.polo?.qsl || 0)
                }
                info.apron = {
                    ...info.apron,
                    inventory: inventoryInfo.previousApron - (info?.apron?.used || 0) + (info?.apron?.qsl || 0)
                }
                info.salmon = {
                    ...info.salmon,
                    inventory: inventoryInfo.previousSalmon - (info?.salmon?.used || 0) + (info?.salmon?.qsl || 0)
                }
                info.tuna = {
                    ...info.tuna,
                    inventory: inventoryInfo.previousTuna - (info?.tuna?.used || 0) + (info?.tuna?.qsl || 0)
                }
            } else {
                const isDayOff = !moment(info.date).weekday() || listHoliday.some(item => moment(item.date).format('YYYY-MM-DD') == info.date)

                if (isDayOff || (info?.polo?.used?.toString() || info?.polo?.qsl?.toString())) {
                    info.polo = {
                        ...info.polo,
                        inventory: ((newData[index - 1].polo?.inventory || 0) - (info.polo?.used || 0) + (info.polo?.qsl || 0))
                    }
                } else {
                    info.polo = {
                        ...info.polo,
                        inventory: null
                    }
                }

                if (isDayOff || (info?.apron?.used?.toString() || info?.apron?.qsl?.toString())) {
                    info.apron = {
                        ...info.apron,
                        inventory: ((newData[index - 1].apron?.inventory || 0) - (info.apron?.used || 0) + (info.apron?.qsl || 0))
                    }
                } else {
                    info.apron = {
                        ...info.apron,
                        inventory: null
                    }
                }

                if (isDayOff || (info?.salmon?.used?.toString() || info?.salmon?.qsl?.toString())) {
                    info.salmon = {
                        ...info.salmon,
                        inventory: ((newData[index - 1].salmon?.inventory || 0) - (info.salmon?.used || 0) + (info.salmon?.qsl || 0))
                    }
                } else {
                    info.salmon = {
                        ...info.salmon,
                        inventory: null
                    }
                }

                if (isDayOff || (info?.tuna?.used?.toString() || info?.tuna?.qsl?.toString())) {
                    info.tuna = {
                        ...info.tuna,
                        inventory: ((newData[index - 1].tuna?.inventory || 0) - (info.tuna?.used || 0) + (info.tuna?.qsl || 0))
                    }
                } else {
                    info.tuna = {
                        ...info.tuna,
                        inventory: null
                    }
                }
            }

            return info
        })
        setDataSource(res);
    }, [dataSource, listHoliday, month, inventoryInfo]);

    const handleOnCell = useCallback((record, title, dataIndex, style) => {
        const isDayOff = !moment(record.date).weekday() || listHoliday.some(item => moment(item.date).format('YYYY-MM-DD') == record.date)

        return {
            record,
            className: isDayOff && cx('dayoff'),
            editable: !isDayOff,
            dataIndex,
            title,
            handleSave,
            style
        }
    }, [handleSave, listHoliday])

    const EditableRow = ({ index, ...props }) => {
        const [form] = Form.useForm();
        return (
            <Form form={form} component={false}>
                <EditableContext.Provider value={form}>
                    <tr {...props} />
                </EditableContext.Provider>
            </Form>
        );
    };

    const EditableCell = ({
        title,
        editable,
        children,
        dataIndex,
        record,
        handleSave,
        ...restProps
    }) => {
        const [editing, setEditing] = useState(false);
        const inputRef = useRef(null);
        const form = useContext(EditableContext);
        useEffect(() => {
            if (editing) {
                inputRef.current.focus();
            }
        }, [editing]);
        const toggleEdit = () => {
            if (!editMode) return
            setEditing(!editing);
            form.setFieldsValue({
                [dataIndex.join('.')]: record?.[dataIndex[0]]?.[dataIndex[1]],
            });
        };
        const save = async () => {
            try {
                const values = await form.validateFields();
                console.log("🚀 ~ values:", values)
                toggleEdit();
                const body = {
                    ...record,
                    [dataIndex[0]]: {
                        ...record?.[dataIndex[0]],
                        [dataIndex[1]]: values[dataIndex.join('.')],
                    },
                }
                console.log("🚀 ~ body:", body)
                handleSave(body);
            } catch (errInfo) {
                console.log('Save failed:', errInfo);
            }
        };

        let childNode = children;
        if (editable) {
            childNode = editing ? (
                <Form.Item
                    style={{
                        margin: 0,
                    }}
                    name={dataIndex.join('.')}
                >
                    <InputCurrency ref={inputRef} onPressEnter={save} onBlur={save} />
                </Form.Item>
            ) : (
                <div
                    className={cx({ 'editable-cell-value-wrap': editMode })}
                    onClick={toggleEdit}
                >
                    {children}
                </div>
            );
        }
        return <td {...restProps}>{childNode}</td>;
    };

    const columns = useMemo(() => [
        {
            title: t('day'),
            dataIndex: "index",
            key: "index",
            align: 'right',
            width: '3%',
            onHeaderCell: (column) => ({ className: cx('revenue-header', 'child') }),
            render: (_, record, index) => index + 1,
            onCell: (record) => ({
                className: (!moment(record.date).weekday() || listHoliday.some(item => moment(item.date).format('YYYY-MM-DD') === record.date)) && cx('dayoff')
            })
        },
        {
            title: t('dow'),
            dataIndex: "date",
            key: "date",
            align: 'right',
            width: '5%',
            onHeaderCell: (column) => ({ className: cx('revenue-header', 'child') }),
            render: (_) => t(moment(_).format('dddd')),
            onCell: (record) => ({
                className: (!moment(record.date).weekday() || listHoliday.some(item => moment(item.date).format('YYYY-MM-DD') === record.date)) && cx('dayoff')
            })
        },
        {
            title: t('poloShirt'),
            className: cx('fish-header', 'parent'),
            children: [
                {
                    title: t('use'),
                    dataIndex: ["polo", "used"],
                    onHeaderCell: (column) => ({ className: cx('fish-header', 'child') }),
                    align: 'right',
                    key: "piece_used",
                    width: '6%',
                    render: (_) => _?.toString() || '　',
                    onCell: (record) => handleOnCell(record, t('use'), ["polo", "used"])
                },
                {
                    title: t('qsl'),
                    dataIndex: ["polo", "qsl"],
                    onHeaderCell: (column) => ({ className: cx('fish-header', 'child') }),
                    align: 'right',
                    width: '6%',
                    key: "piece_qsl",
                    render: (_) => _?.toString() || '　',
                    onCell: (record) => handleOnCell(record, t('qsl'), ["polo", "qsl"])
                },
                {
                    title: t('inventory'),
                    dataIndex: ["polo", "inventory"],
                    onHeaderCell: (column) => ({ className: cx('fish-header', 'child') }),
                    align: 'right',
                    key: "inventory",
                    width: '5%',
                    render: (_) => _?.toString() || '',
                    onCell: (record) => ({
                        className: (!moment(record.date).weekday() || listHoliday.some(item => moment(item.date).format('YYYY-MM-DD') === record.date)) && cx('dayoff')
                    }),
                },
            ]
        },
        {
            title: t('apron'),
            className: cx('fish-header', 'parent'),
            children: [
                {
                    title: t('use'),
                    dataIndex: ["apron", "used"],
                    onHeaderCell: (column) => ({ className: cx('fish-header', 'child') }),
                    align: 'right',
                    key: "piece_used",
                    width: '6%',
                    render: (_) => _?.toString() || '　',
                    onCell: (record) => handleOnCell(record, t('use'), ["apron", "used"])
                },
                {
                    title: t('qsl'),
                    dataIndex: ["apron", "qsl"],
                    onHeaderCell: (column) => ({ className: cx('fish-header', 'child') }),
                    align: 'right',
                    width: '6%',
                    key: "piece_qsl",
                    render: (_) => _?.toString() || '　',
                    onCell: (record) => handleOnCell(record, t('qsl'), ["apron", "qsl"])
                },
                {
                    title: t('inventory'),
                    dataIndex: ["apron", "inventory"],
                    onHeaderCell: (column) => ({ className: cx('fish-header', 'child') }),
                    align: 'right',
                    key: "inventory",
                    width: '5%',
                    render: (_) => _?.toString() || '',
                    onCell: (record) => ({
                        className: (!moment(record.date).weekday() || listHoliday.some(item => moment(item.date).format('YYYY-MM-DD') === record.date)) && cx('dayoff')
                    }),
                },
            ]
        },
        {
            title: `${t('fishSalmon')} (${t('piece')})`,
            className: cx('fish-header', 'parent'),
            children: [
                {
                    title: t('use'),
                    dataIndex: ["salmon", "used"],
                    onHeaderCell: (column) => ({ className: cx('fish-header', 'child') }),
                    align: 'right',
                    key: "piece_used",
                    width: '6%',
                    render: (_) => _?.toString() || '　',
                    onCell: (record) => handleOnCell(record, t('use'), ["salmon", "used"])
                },
                {
                    title: t('qsl'),
                    dataIndex: ["salmon", "qsl"],
                    onHeaderCell: (column) => ({ className: cx('fish-header', 'child') }),
                    align: 'right',
                    width: '6%',
                    key: "piece_qsl",
                    render: (_) => _?.toString() || '　',
                    onCell: (record) => handleOnCell(record, t('qsl'), ["salmon", "qsl"])
                },
                {
                    title: t('inventory'),
                    dataIndex: ["salmon", "inventory"],
                    onHeaderCell: (column) => ({ className: cx('fish-header', 'child') }),
                    align: 'right',
                    key: "inventory",
                    width: '5%',
                    render: (_) => _?.toString() || '',
                    onCell: (record) => ({
                        className: (!moment(record.date).weekday() || listHoliday.some(item => moment(item.date).format('YYYY-MM-DD') === record.date)) && cx('dayoff')
                    }),
                },
            ]
        },
        {
            title: `${t('fishTuna')} (${t('piece')})`,
            className: cx('fish-header', 'parent'),
            children: [
                {
                    title: t('use'),
                    dataIndex: ["tuna", "used"],
                    onHeaderCell: (column) => ({ className: cx('fish-header', 'child') }),
                    align: 'right',
                    key: "piece_tuna_used",
                    width: '6%',
                    render: (_) => _?.toString() || '　',
                    onCell: (record) => handleOnCell(record, t('use'), ["tuna", "used"])
                },
                {
                    title: t('qsl'),
                    dataIndex: ["tuna", "qsl"],
                    onHeaderCell: (column) => ({ className: cx('fish-header', 'child') }),
                    align: 'right',
                    width: '6%',
                    key: "piece_tuna_qsl",
                    render: (_) => _?.toString() || '　',
                    onCell: (record) => handleOnCell(record, t('qsl'), ["tuna", "qsl"])
                },
                {
                    title: t('inventory'),
                    dataIndex: ["tuna", "inventory"],
                    onHeaderCell: (column) => ({ className: cx('fish-header', 'child') }),
                    align: 'right',
                    key: "tuna_inventory",
                    width: '5%',
                    render: (_) => _?.toString() || '',
                    onCell: (record) => ({
                        className: (!moment(record.date).weekday() || listHoliday.some(item => moment(item.date).format('YYYY-MM-DD') === record.date)) && cx('dayoff')
                    }),
                },
            ]
        },
    ], [t, listHoliday, handleOnCell])

    const analytic = useMemo(() => {
        let totalPoloUse = 0
        let totalApronUse = 0
        let totalSalmonUse = 0
        let totalTunaUse = 0
        let totalPoloQsl = 0
        let totalApronQsl = 0
        let totalSalmonQsl = 0
        let totalTunaQsl = 0

        dataSource.forEach(({
            polo,
            apron,
            salmon,
            tuna
        }) => {
            totalPoloUse += polo?.used || 0
            totalApronUse += apron?.used || 0
            totalSalmonUse += salmon?.used || 0
            totalTunaUse += tuna?.used || 0
            totalPoloQsl += polo?.qsl || 0
            totalApronQsl += apron?.qsl || 0
            totalSalmonQsl += salmon?.qsl || 0
            totalTunaQsl += tuna?.qsl || 0
        });

        return {
            totalPoloUse,
            totalApronUse,
            totalSalmonUse,
            totalTunaUse,
            totalPoloQsl,
            totalApronQsl,
            totalSalmonQsl,
            totalTunaQsl,
        }
    }, [dataSource])

    const components = {
        body: {
            row: EditableRow,
            cell: EditableCell,
        },
    };

    const onImport = async (values) => {
        try {
            const reader = new FileReader();
            const rABS = !!reader.readAsBinaryString;
            reader.onload = async (e) => {
                /* Parse data */
                const bstr = e.target.result;
                const wb = XLSX.read(bstr, { type: rABS ? "binary" : "array" });
                /* Get first worksheet */
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                /* Convert array of arrays */
                const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
                if (Array.isArray(data) && data.length >= 4) {
                    let payload = []
                    let error = ''
                    data.filter(item => item.length > 0 && item).forEach((item, idx) => {
                        if (idx > 1) {
                            const [
                                countDate,
                                dow,
                                poloUsed,
                                poloQsl,
                                poloInventory,
                                apronUsed,
                                apronQsl,
                                apronInventory,
                                salmonUsed,
                                salmonQsl,
                                salmonInventory,
                                tunaUsed,
                                tunaQsl,
                                tunaInventory,
                            ] = item

                            payload.push({
                                date: moment(month, 'YYYY-MM').date(countDate).format('YYYY-MM-DD'),
                                polo: {
                                    used: poloUsed,
                                    qsl: poloQsl,
                                    inventory: poloInventory
                                },
                                apron: {
                                    used: apronUsed,
                                    qsl: apronQsl,
                                    inventory: apronInventory
                                },
                                salmon: {
                                    used: salmonUsed,
                                    qsl: salmonQsl,
                                    inventory: salmonInventory
                                },
                                tuna: {
                                    used: tunaUsed,
                                    qsl: tunaQsl,
                                    inventory: tunaInventory
                                }
                            })

                        }
                    })
                    if (!error) {
                        try {
                            await inventoryApi.createInventory({
                                shop,
                                month,
                                list: payload
                            })
                            notify("success", t('notification'), t('importSuccess'));
                            onSearch()
                            closeModalImport()
                        } catch (error) {
                            notify("error", t('error'), t('hasError'));
                        }
                    } else {
                        notify("error", t('error'), error || t('hasError'));
                    }

                } else toast.error("Import error!")

                /* Update state */
            };
            if (rABS) reader.readAsBinaryString(values.upload[0].originFileObj);
            else reader.readAsArrayBuffer(values.upload[0].originFileObj);
        } catch (error) {
            // toast.error(error?.response?.data || ERROR_OCCUR)
        } finally {
        }
    }

    return (
        <>
            <ModalImport loading={loading} show={openImport} onFinish={onImport} form={formImport} onCancel={closeModalImport} />
            <Card>
                <div className={cx('button-area')}>
                    {
                        !editMode && <Button icon={<EditOutlined />} type='primary' size='large' onClick={toggleModeEdit}>{t('edit')}</Button>
                    }
                    {
                        editMode &&
                        <Space>
                            <Button loading={loading} icon={<FileSyncOutlined />} type='primary' size='large' onClick={saveEdit}>{t('save')}</Button>
                            <Button loading={loading} icon={<CloseOutlined />} danger size='large' onClick={cancelEdit}>{t('cancel')}</Button>
                        </Space>
                    }
                    {
                        rolesImport.includes(+getUser().role) &&
                        <Space>
                            <Button size="large" icon={<CloudUploadOutlined />} onClick={onOpenImport} >
                                {t('import')}
                            </Button>
                            <Table2XLSX {...{ month, dataSource, shopInfo }} />
                        </Space>
                    }

                </div>

                <div className={cx('budget-area')}>
                    <Space direction="vertical">
                        <Descriptions bordered>
                            <Descriptions.Item label={t('lastMonthPoloshirt')}>{+inventoryInfo.previousPolo}</Descriptions.Item>
                        </Descriptions>
                        <Descriptions bordered>
                            <Descriptions.Item label={t('lastMonthApron')}>{+inventoryInfo.previousApron}</Descriptions.Item>
                        </Descriptions>
                        <Descriptions bordered>
                            <Descriptions.Item label={t('lastMonthSalmonInventory')}>{+inventoryInfo.previousSalmon}</Descriptions.Item>
                        </Descriptions>
                        <Descriptions bordered>
                            <Descriptions.Item label={t('lastMonthTunaInventory')}>{+inventoryInfo.previousTuna}</Descriptions.Item>
                        </Descriptions>
                    </Space>
                </div>
                <Table
                    components={components}
                    rowClassName={() => cx('editable-row')}
                    size="small"
                    columns={columns.map(item => {
                        if (item.children && item.children.some(child => child.children)) {
                            return {
                                ...item,
                                children: item.children.map(child => ({
                                    ...child,
                                    children: (child?.children || []).filter(subChild => !subChild.isHide)
                                }))
                            }
                        }

                        return item;
                    })}
                    bordered
                    dataSource={dataSource}
                    pagination={false}
                    sticky
                    summary={(pageData) => {
                        return (
                            <Table.Summary fixed='bottom'>
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={0} colSpan={2}>
                                        <strong>{t('sum')}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell align='right' >
                                        <strong>{analytic.totalPoloUse}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell align='right' >
                                        <strong>{analytic.totalPoloQsl}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell align='right' >
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell align='right' >
                                        <strong>{analytic.totalApronUse}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell align='right' >
                                        <strong>{analytic.totalApronQsl}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell align='right' >
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell align='right' >
                                        <strong>{analytic.totalSalmonUse}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell align='right' >
                                        <strong>{analytic.totalSalmonQsl}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell align='right' >
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell align='right' >
                                        <strong>{analytic.totalTunaUse}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell align='right' >
                                        <strong>{analytic.totalTunaQsl}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell align='right' >
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            </Table.Summary>
                        );
                    }}
                />
                <br />
            </Card>
        </>
    )
}